import { useLinkedProjectIntegrations } from '@hooks/useLinkedProjectIntegrations'
import { useSearchTimeLoggerTasks } from '@hooks/useSearchTasks'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavItem } from '../../components/NavItem'
import { Column } from './Column'

interface Props {
  autoSelect: boolean
  displaySearch?: boolean
  heading?: string
  onItemClick: (item: { id: number; name: string }) => void
  onSearchChange: (search: string) => void
  onSearchToggle: (displayed: boolean) => void
  onTimeLoggerClick: (label: string) => void
  projectId: number
  searchValue?: string
  shown: boolean
}

export const TaskSourcesColumn: FC<Props> = memo(
  ({
    autoSelect,
    displaySearch,
    heading,
    onItemClick,
    onSearchChange,
    onSearchToggle,
    onTimeLoggerClick,
    projectId,
    searchValue = '',
    shown,
  }) => {
    const { t } = useTranslation()
    const [consideredAutoSelect, setConsideredAutoSelect] = useState(false)

    const projectIntegrationsResult = useLinkedProjectIntegrations({
      enabled: shown,
      projectId,
    })

    const timeLoggerTasksResult = useSearchTimeLoggerTasks({
      enabled: shown,
      projectId,
    })

    const isLoading =
      projectIntegrationsResult.isLoading || timeLoggerTasksResult.isLoading

    const items = useMemo(() => {
      const integrationItems = (projectIntegrationsResult.data ?? []).map(
        (projectIntegration) => ({
          key: `integration-${projectIntegration.id}`,
          name: projectIntegration.name,
          onClick: () => onItemClick(projectIntegration),
        }),
      )

      const hasTimeLoggerTasks =
        (timeLoggerTasksResult.data?.pages.at(0)?.meta?.total ?? 0) > 0

      if (!hasTimeLoggerTasks) return integrationItems

      const timeLoggerLabel = t('common.timeLogger')

      return [
        ...integrationItems,
        {
          key: 'time-logger',
          name: timeLoggerLabel,
          onClick: () => onTimeLoggerClick(timeLoggerLabel),
        },
      ]
    }, [
      projectIntegrationsResult.data,
      timeLoggerTasksResult.data,
      onItemClick,
      onTimeLoggerClick,
      t,
    ])

    useEffect(() => {
      if (!shown || !autoSelect || consideredAutoSelect || isLoading) return

      setConsideredAutoSelect(true)

      if (items.length === 1) items[0].onClick()
    }, [shown, autoSelect, consideredAutoSelect, isLoading, items])

    const filteredItems = useMemo(() => {
      if (!searchValue?.trim()) return items

      return items.filter((item) =>
        item.name.toLowerCase().includes(searchValue.trim().toLowerCase()),
      )
    }, [items, searchValue])

    if (!shown) return null

    return (
      <Column
        heading={heading}
        displaySearch={displaySearch}
        isFetching={
          projectIntegrationsResult.isFetching ||
          timeLoggerTasksResult.isFetching ||
          isLoading
        }
        isInitiallyLoading={isLoading}
        items={filteredItems}
        loadMoreItems={() => undefined}
        activeFilterEnabled={false}
        onSearchToggle={onSearchToggle}
        onSearchChange={onSearchChange}
        renderItem={(item) => (
          <NavItem
            key={item.key}
            label={item.name}
            onClick={item.onClick}
            showImage={false}
            skip={false}
          />
        )}
        searchValue={searchValue}
        subheading={t('features.timeLogging.taskSelection.taskSources')}
        totalItems={filteredItems.length}
      />
    )
  },
)

TaskSourcesColumn.displayName = 'TaskSourcesColumn'
